import fetcher from '@app/utils/fetcher';
import CheckoutBreadcrumbs from '@features/purchasing/components/CheckoutBreadcrumbs';
import ToCheckout from '@features/purchasing/my-basket/ToCheckout';
import { ICheckoutStep } from '@features/purchasing/types';
import Loader from '@store/components/Loader';
import { emit } from '@store/events';
import useConfiguration from '@store/hooks/useConfiguration';
import ky from 'ky';
import React from 'react';
import useSWR from 'swr';

import Buttons from './Buttons';
import Lines from './Lines';
import Totals from './Totals';
import { IGetResponse, ILinks, OnUpdateLine } from './types';

interface IProps {
  basketId: string;
  title: string;
  basketName: string;
  steps: ICheckoutStep[];
  nextStepUrl: string;
  links?: ILinks;
  apiUrls: { get: string; setLine: string };
}

export default function Index(props: IProps) {
  const { xsrf } = useConfiguration();
  const { data, error, mutate } = useSWR(props.apiUrls.get, url => fetcher<IGetResponse>(url), {
    revalidateOnFocus: false,
    errorRetryCount: 0,
  });

  const onUpdateLine: OnUpdateLine = async ({ productId, optionId, unitCount }) => {
    const { basketId } = props;

    const fd = new FormData();
    fd.set('basketId', basketId);
    fd.set('productId', productId);
    fd.set('unitCount', `${unitCount}`);
    if (optionId) fd.set('optionId', optionId);

    const response = await ky.put(props.apiUrls.setLine, {
      body: fd,
      headers: { 'X-CSRF-TOKEN': xsrf },
      throwHttpErrors: false,
    });

    if (!response.ok) {
      console.error('Failed to set item: ' + (await response.text()));
      return;
    }

    const json = await response.json();
    await mutate();
    if (json.notification) emit.alert(json.notification);
    if (json.widgetUrl) emit.basket.reload(json.widgetUrl);
  };

  if (!data && !error) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader className="text-base text-primary" />
      </div>
    );
  }

  if (error) {
    return <></>;
  }

  return (
    <div className="max-w-5xl flex flex-col mx-auto">
      {props.steps && <CheckoutBreadcrumbs steps={props.steps} />}

      <h1 className="flex flex-col laptop:flex-row items-center laptop:items-baseline laptop:justify-center gap-x-2">
        <small className="text-primary-dark">{props.title}</small>
        {props.basketName}
      </h1>

      {props.links && (
        <Buttons
          {...props.links}
          basketName={props.basketName}
          hasLines={(data?.lines?.length ?? 0) > 0}
        />
      )}
      <Lines lines={data?.lines || []} onUpdate={onUpdateLine} />
      {data && (
        <>
          <Totals
            summaryBeforeVat={data.summaryBeforeVat}
            summaryAfterVat={data.summaryAfterVat}
            summaryVat={data.summaryVat}
            vatLevels={data.vatLevels}
            customerDiscount={data.customerDiscount}
          />
          <ToCheckout hasLines={data.lines.length > 0} nextStepUrl={props.nextStepUrl} />
        </>
      )}
    </div>
  );
}
